import Stroke from 'ol/style/Stroke';
import Style, {StyleFunction} from 'ol/style/Style';
import Circle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Collection from 'ol/Collection';
import Draw from 'ol/interaction/Draw';
import Select, {SelectEvent} from 'ol/interaction/Select';
import Modify, {ModifyEvent} from 'ol/interaction/Modify';
import MultiPoint from 'ol/geom/MultiPoint';
import {Vector as LayerVector} from 'ol/layer';
import {pointerMove, shiftKeyOnly, singleClick} from 'ol/events/condition';
import Feature, {FeatureLike} from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import {clone, first, isEmpty} from 'lodash-es';
import {createEmpty, extend, Extent, isEmpty as isEmptyExtent} from "ol/extent";
import KlDrawZoneMapUtils, {
    EDrawZoneMapFeaturePropertyKey
} from '@/app/shared/components/kl-draw-zone-map/kl-draw-zone-map-utils';
import ImageWMS from 'ol/source/ImageWMS';
import ImageLayer from 'ol/layer/Image';
import KlDrawZoneSidebar from '@/app/shared/components/kl-draw-zone-map/components/kl-draw-zone-sidebar/kl-draw-zone-sidebar.vue';
import {
    EDrawZoneMapState
} from '@/app/shared/components/kl-draw-zone-map/components/kl-draw-zone-sidebar/kl-draw-zone-sidebar';
import {computed, defineComponent, nextTick, onMounted, provide, ref, useTemplateRef, watch} from 'vue';
import {useDrawZoneContext} from '@/app/shared/components/kl-draw-zone-map/kl-draw-zone-context';
import {IpproMap} from '@/plugins/ipproComponents';
import {Coordinate} from 'ol/coordinate';
import KlDrawZoneGeoLocation from './components/kl-draw-zone-geolocation.vue';
import {DownloadUrl} from "@/api/klip-api.proxy";
import * as yup from 'yup';
import KlDrawZoneActions from '@/app/shared/components/kl-draw-zone-map/components/kl-draw-zone-actions.vue';

export interface IDrawZoneOrgInfo {
    isKlim: boolean;
    guid: string;
    namespace: string;
    isActive: boolean;
    isArchived: boolean;
    willBeActivatedOn?: Date;
    editLink: any;
}

export interface IDrawZoneGeometryInfo {
    title?: string;
    isActive: boolean;
    subtitle: string;
    subtitle2?: string;
    version?: string | undefined;
    lastModificationDate?: string | undefined;
    precision?: string | undefined;
    downloadUrls?: Array<DownloadUrl> | undefined;
}

export interface IDrawZoneMapGeometry {
    name?: string;
    coordinates?: number[][][] | number[][][][]; // array of polygon[] | multipolygon[]; polygon = array of coordinates
    wkt?: string; // use coordinates OR wkt, NOT both
    isValid?: boolean;
    orgZoneInfo?: IDrawZoneOrgInfo;
    geometryInfo?: IDrawZoneGeometryInfo;
}

export default defineComponent({
    name: 'KlDrawZoneMap',
    components: {KlDrawZoneActions, KlDrawZoneGeoLocation, KlDrawZoneSidebar },
    emits: ['input', 'zone-price', 'state', 'remove-geometry', 'toggle-show-active'],
    props: {
        inputGeometries: { type: Array as () =>  IDrawZoneMapGeometry[], default: (): IDrawZoneMapGeometry[] => [], },
        modMaprequestZone: { type: Boolean, default: false, },
        modToggleFullscreen: { type: Boolean, default: true, },
        modDisabled: { type: Boolean, default: false, },
        modError: { type: Boolean, default: false },
        loadingMessage: { type: String, default: 'Zone wordt gevalideerd' },
        reference: { type: String, default: '', },
        rules: { type: Object as () => yup.Schema, default: undefined, },
        includeImkl: { type: Boolean, default: false, },
        forMaprequest: { type: Boolean, default: false, },
        noConfirmation: { type: Boolean, default: false, },
        modShowKmHmToggle: { type: Boolean, default: true, },
        modShowGpsLocationButton: { type: Boolean, default: false, },
        modEnableMultiZones: { type: Boolean, default: false, },
        showMap: { type: Boolean, default: false, },

        forKlbZones: { type: Boolean, default: false, },
        canAddKlbZone: { type: Boolean, default: false, },
        canAddKlimZone: { type: Boolean, default: false, },
        addKlbZoneLink: { type: Object as () => any, default: null, },
        addKlimZoneLink: { type: Object as () => any, default: null, },
        showKlbArchivedZoneToggle: { type: Boolean, default: false, },
        klbArchivedZoneToggleValue: { type: Boolean, default: false, },

        forGeometry: { type: Boolean, default: false },
        showRemoveGeometry: { type: Boolean, default: false },

        projection: { type: String, default: import.meta.env.VITE_MAP_EPSG_DEFAULT, }, // ['EPSG:31370', 'EPSG:3812']
        disableReprojectionWarning: { type: Boolean, default: false },

        useSidebar: { type: Boolean, default: true }, // TODO: betere benaming!! = ..
        useInputGeometriesForVisibleArea: { type: Boolean, default: false },

        simpleDrawMode: { type: Boolean,  default: false },
    },
    setup(props, {emit}) {

        const _context = useDrawZoneContext();
        provide('kl-draw-zone-map-context', _context);

        const sidebar = useTemplateRef<InstanceType<typeof KlDrawZoneSidebar>>('sidebar');
        const map = useTemplateRef<InstanceType<typeof IpproMap>>('map');

        let _mapInstance: any = null;

        const _state = ref<EDrawZoneMapState>();

        const isLoading = ref<boolean>(false);

        let kmhmLayer: ImageLayer<ImageWMS> = null;
        const kmhmLoadCounter = ref<number>(0);

        const kmhmEnabled = ref<boolean>(false);
        const kmhmHasError = ref<boolean>(false);

        const allowMultiPolygons = computed(() => {
            return props.forKlbZones || props.forGeometry;
        });

        // TODO: REVIEW
        const _enableSelectInteraction = computed((): boolean => {
           return !props.forGeometry;
        });
        const _enableModifyInteraction = computed((): boolean => {
           //return !props.forGeometry;
           return true;
        });


        const kmhmIsLoading = computed((): boolean => {
            return kmhmLoadCounter.value > 0;
        });

        const disableDeleteAction = computed((): boolean => {
            return !_context.currentFeatureRef.value;
        });

        const showSearch = computed((): boolean => {
            // simple hack to fix possible overlapping controls
            // problem:
            // - simpleDrawMode only uses action buttons (top right) & search (top left)
            // - sidepanel is only visible during import and can push to search & action controls over each other
            return !props.simpleDrawMode || (_state.value !== EDrawZoneMapState.import);
        });

        let drawInteraction: Draw = null;
        let selectInteraction: Select = null;
        let modifyInteraction: Modify = null;

        watch(
            () => props.showMap,
            (showMap: boolean) => {
                if (showMap) {
                    nextTick(() => {
                        _mapInstance.updateSize();
                        if (_context.getCurrentFeature()) {
                            map.value.zoomToFeature(_context.getCurrentFeature(), 0);
                        }
                    });
                }
            },
            {immediate: false, deep: false});

        watch(
            kmhmEnabled,
            (checked: boolean) => {
                toggleKmHmLayer(checked);
            },
        )

        // INFO:
        // A better way would be to watch the props.inputGeometries, but this is to complicated in the current setup.
        // The geometries can be modified inside the map component, and can lead to infinite update loops.
        const updateInputGeometries = async (inputGeometries: IDrawZoneMapGeometry[]) => {
            // wait until component is properly initialized
            // initial 'inputGeometries' can be set in the props
            if (!_mapInstance) {
                return;
            }
            await _applyInputGeometries(inputGeometries);
        }

        const _applyInputGeometries = async (geometries: IDrawZoneMapGeometry[]) => {
            if (props.useSidebar) {
                await sidebar.value?.initZones(geometries);
            }
            if (props.useInputGeometriesForVisibleArea) {
                console.assert(!geometries || geometries.length === 1, 'useInputGeometriesForVisibleArea: current useCase only support single inputGeometry', geometries);
                _setCurrentVisibleArea(first(geometries));
            }
        }

        const _createDrawInteraction = (): Draw => {
            return new Draw({
                type: 'Polygon',
                stopClick: true,
                style: [
                    new Style({
                        stroke: new Stroke({
                            color: 'rgba(78, 79, 112, 0.8)',
                            width: 1,
                        }),
                        fill: new Fill({
                            color: 'rgba(137, 138, 156, 0.4)',
                        }),
                    }),
                    new Style({
                        image: new Circle({
                            radius: 6,
                            fill: new Fill({
                                color: 'rgba(137, 138, 156, 0.4)',
                            }),
                            stroke: new Stroke({
                                color: 'rgba(78, 79, 112, 0.8)',
                                width: 1,
                            }),
                        }),
                    }),
                ],
                geometryFunction: (coordinates, geometry) => {
                    // Only used to show the 'opp=xx' while drawing a new polygon.
                    // can't find a proper openlayers way to listen to changes on the add-interaction

                    const firstRingCoordinates: Coordinate[] = coordinates[0] as Coordinate[];

                    const newCoordinates = clone(firstRingCoordinates);
                    newCoordinates.push(firstRingCoordinates[0]); // auto-close polygon

                    if (!geometry) {
                        geometry = new Polygon([newCoordinates]);
                    }
                    else {
                        geometry.setCoordinates([newCoordinates]);
                    }

                    _updateDrawingPolygon(geometry as Polygon);
                    return geometry;
                }
            });
        }

        const _createSelectInteraction = (): Select => {
            let selectStyle: StyleFunction = null;

            if (props.forKlbZones) {
                selectStyle = (f: FeatureLike) => {
                    const orgZoneInfo: IDrawZoneOrgInfo = f.get(EDrawZoneMapFeaturePropertyKey.orgZoneInfo);
                    return [
                        new Style({
                            stroke: new Stroke({
                                color: orgZoneInfo?.isArchived ? 'rgba(34, 85, 89, 1)' : '#0055cc',
                                width: 3,
                                lineDash: (orgZoneInfo?.isActive || orgZoneInfo?.isArchived) ? undefined : [5, 3],
                            }),
                            fill: new Fill({
                                color: orgZoneInfo?.isArchived ? 'rgba(34, 85, 89, 0.85)' : 'rgba(0, 85, 204, 0.85)',
                            }),
                        })
                    ]
                };
            }
            else {
                selectStyle = (f: FeatureLike) => [
                    new Style({
                        stroke: new Stroke({
                            color: 'rgba(21, 155, 170, 0.8)',
                            width: 1,
                        }),
                        fill: new Fill({
                            color: 'rgba(21, 155, 170, 0.4)',
                        }),
                    }),
                    new Style({
                        image: new Circle({
                            radius: 6,
                            fill: new Fill({
                                color: 'rgba(137, 138, 156, 0.4)',
                            }),
                            stroke: new Stroke({
                                color: 'rgba(78, 79, 112, 0.8)',
                                width: 1,
                            }),
                        }),
                        geometry: (feature) => {
                            // USE: put circles on the points (= only for styling)

                            if (!feature.getGeometry() || feature.getGeometry().getType() !== 'Polygon') {
                                return null;
                            }

                            const polygon = feature.getGeometry() as Polygon;

                            if (polygon.getLinearRingCount() === 0) {
                                return null;
                            }

                            // return the coordinates of the first ring of the polygon (when at least 4)
                            const coordinates = polygon.getLinearRing(0).getCoordinates();
                            if (coordinates.length < 4) {
                                return null;
                            }
                            return new MultiPoint(coordinates);
                        },
                    }),
                ];
            }

            let selectCondition = pointerMove;
            if (!props.forKlbZones) {
                selectCondition = (event) => {
                    if (shiftKeyOnly(event)) {
                        // Do NOT use this event for selecting/deselecting. Will also trigger while deleting points with 'shift' during modify.
                        return false;
                    }
                    return singleClick(event);
                };
            }

            return new Select({
                features: _context.selectedFeatures,
                condition: selectCondition,
                style: selectStyle,
            });
        }

        const _removeModifyInteraction = () => {
            if (modifyInteraction) {
                modifyInteraction.setActive(false);
                _mapInstance.removeInteraction(modifyInteraction);
            }
            modifyInteraction = null;
        }

        const _createModifyInteraction = (): Modify => {
            return new Modify({
                features: _context.selectedFeatures,
                deleteCondition: (event) => {
                    return shiftKeyOnly(event);
                },
            });
        }


        const _addKmHmLayer = () => {
            const source = new ImageWMS({
                url: 'https://opendata.apps.mow.vlaanderen.be/opendata-geoserver/awv/wms',
                params: {'LAYERS': 'Referentiepunten'},
            });

            source.on('imageloaderror', () => {
                kmhmLoadCounter.value--;
                kmhmHasError.value = true;
            });
            source.on('imageloadstart', () => {
                kmhmLoadCounter.value++;
                kmhmHasError.value = false;
            });
            source.on('imageloadend', () => {
                kmhmLoadCounter.value--;
                kmhmHasError.value = false;
            });

            kmhmLayer = new ImageLayer({
                source: source,
                visible: false,
            });

            _mapInstance.addLayer(kmhmLayer);
        }

        const _addDrawingLayer = () => {
            let layerStyle: StyleFunction = null;

            if (props.forKlbZones || props.forGeometry) {
                layerStyle = (f: FeatureLike) => {
                    const orgZoneInfo: IDrawZoneOrgInfo = f.get(EDrawZoneMapFeaturePropertyKey.orgZoneInfo);
                    const geometryInfo: IDrawZoneGeometryInfo = f.get(EDrawZoneMapFeaturePropertyKey.geometryInfo);
                    return [
                        new Style({
                            stroke: new Stroke({
                                color: orgZoneInfo?.isArchived ? 'rgba(34, 85, 89, 1)' : '#0055cc',
                                width: 2,
                                lineDash: orgZoneInfo?.isActive || orgZoneInfo?.isArchived || geometryInfo?.isActive ? undefined : [5, 3],
                            }),
                            fill: new Fill({
                                color: orgZoneInfo?.isArchived ? 'rgba(34, 85, 89, 0.6)' : 'rgba(0, 85, 204, 0.6)',
                            }),
                        })];
                }
            }
            else {
                layerStyle = (f: FeatureLike) => [new Style({
                    stroke: new Stroke({
                        color: 'rgba(78, 79, 112, 0.8)',
                        width: 1,
                    }),
                    fill: new Fill({
                        color: 'rgba(137, 138, 156, 0.4)',
                    }),
                })];
            }

            const drawLayer = new LayerVector({
                source: _context.source,
                style: layerStyle,
            });
            _mapInstance.addLayer(drawLayer);
        }


        const _removeDrawInteraction = () => {
            if (drawInteraction) {
                drawInteraction.setActive(false);
                _mapInstance.removeInteraction(drawInteraction);
            }
            drawInteraction = null;
        }

        const _addDrawInteraction = () => {
            _removeDrawInteraction();
            drawInteraction = _createDrawInteraction();

            drawInteraction.setActive(true);
            _mapInstance.addInteraction(drawInteraction);

            drawInteraction.on('drawend', async (e) => {
                _context.selectFeature(e.feature);

                await sidebar.value?.onDrawInteraction_DrawEnd(e.feature);
            });
        }

        const _removeSelectInteraction = () => {
            if (selectInteraction) {
                selectInteraction.setActive(false);
                _mapInstance.removeInteraction(selectInteraction);
            }
            selectInteraction = null;
        }

        const _addSelectInteraction = () => {
            if (!_enableSelectInteraction.value) {
                return;
            }

            _removeSelectInteraction();
            selectInteraction = _createSelectInteraction();

            //selectInteraction.setActive(true);
            selectInteraction.setActive(!props.simpleDrawMode); // HACK: we gebruiken de selectInteraction voor de styling, maar niet meer om te selecteren
            _mapInstance.addInteraction(selectInteraction);

            selectInteraction.on('select', async (selectEvent: SelectEvent) => {
                await sidebar.value?.onSelectInteraction_Select(selectEvent.selected, selectEvent.deselected);
            });
        }

        const _addModifyInteraction = () => {
            if (!_enableModifyInteraction.value) {
                return;
            }

            _removeModifyInteraction();
            modifyInteraction = _createModifyInteraction();

            modifyInteraction.setActive(true);
            _mapInstance.addInteraction(modifyInteraction);

            if (props.noConfirmation) {
                modifyInteraction.on('modifyend', async (event: ModifyEvent) => {
                    await sidebar.value?.onModifyInteraction_ModifyEnd(event.features.item(0));
                });
            }
        }

        const _updateDrawingPolygon = (polygon: Polygon) => {
            const area = KlDrawZoneMapUtils.getPolygonArea(polygon);
            _context.updateCurrentArea(area);
        }
        const onStateChange = (state: EDrawZoneMapState) => {
            _setState(state);
        }

        const onZoomToFeature = (feature: Feature) => {
            if (feature && feature.getGeometry()) {
                map.value.zoomToFeature(feature, 500);
            }
        }

        const onZoomToFeatures = (features: Collection<Feature>) => {
            if (isEmpty(features)) {
                return;
            }

            const extent: Extent = createEmpty();
            features.forEach((feature: Feature) => {
                if (feature.getGeometry() && feature.getGeometry().getExtent()) {
                    extend(extent, feature.getGeometry().getExtent())
                }
            });

            if (!isEmptyExtent(extent)) {
                map.value.zoomToExtent(extent, 500);
            }
        }

        const onLoading = (loading: boolean) => {
            isLoading.value = loading;
        }

        const onInput = (newGeometries: IDrawZoneMapGeometry[]) => {
            emit('input', newGeometries);
        }

        const onZonePrice = (price: number) => {
            emit('zone-price', price);
        }

        const _setState = (state: EDrawZoneMapState) => {
            _state.value = state;

            if (state === EDrawZoneMapState.overview) {
                _addSelectInteraction();

                _removeDrawInteraction();
                _removeModifyInteraction();

            } else if (state === EDrawZoneMapState.create) {
                _addDrawInteraction();

                _removeModifyInteraction();
                _removeSelectInteraction();

            } else if (state === EDrawZoneMapState.edit) {
                _addSelectInteraction();
                _addModifyInteraction();

                _removeDrawInteraction();

            } else if (state === EDrawZoneMapState.import) {
                _removeDrawInteraction();
                _removeModifyInteraction();
                _removeSelectInteraction();
            }

            emit('state', state);
        }

        const onSearchOnExtent = (extent: Extent) => {
            if (extent) {
                map.value.zoomToExtent(extent, 500);
            }
        }

        const onSearchOnCoordinate = (coordinate: number[]) => {
            if (coordinate) {
                map.value.zoomToCoordinates(coordinate, 500);
            }
        }

        const onRemoveGeometry = () => {
            emit('remove-geometry');
        }

        const onKlbToggleShowActive = (newValue: boolean) => {
            emit('toggle-show-active', newValue);
        }

        onMounted(async () => {
            _context.clear();
            _context.setProjection(props.projection);

            // map = getCurrentInstance().proxy.$refs['map'];
            // sidebar = getCurrentInstance().proxy.$refs['sidebar'];
            _mapInstance = map.value.instance;

            _addKmHmLayer();
            _addDrawingLayer();

            _addKlbZonesSelectHandler();

            await _applyInputGeometries(props.inputGeometries);
        })

        const _addKlbZonesSelectHandler = () => {
            if (!props.forKlbZones) {
                return;
            }

            _mapInstance.on('singleclick', async (iets: any) => {
                if (_context.selectedFeatures.getLength() === 1) {
                    sidebar.value?.editKlbZone(_context.selectedFeatures.item(0).get(EDrawZoneMapFeaturePropertyKey.drawZoneId))
                }
            });
        }

        const toggleKmHmLayer = (checked: boolean) => {
            kmhmEnabled.value = checked;
            kmhmLayer?.setVisible(checked);
        }

        const getCurrentVisibleArea = (): number[][][] => {
            const extent = _mapInstance.getView().calculateExtent(_mapInstance.getSize());
            return [[
                [extent[0], extent[1]],
                [extent[2], extent[1]],
                [extent[2], extent[3]],
                [extent[0], extent[3]],
                [extent[0], extent[1]],
            ]];
        }

        const _setCurrentVisibleArea = (geometry: IDrawZoneMapGeometry) => {
            if (!geometry) {
                return;
            }

            const feature = KlDrawZoneMapUtils.createFeatureFromDrawZoneMapGeometry(geometry);
            if (!feature) {
                return;
            }

            _mapInstance.getView().fit(feature.getGeometry(), {
                size: _mapInstance.getSize(),
                duration: 0,
            });
        }

        ////////////////////////////////////////////////////////////////////////////////
        // refactor using a shared context iso subcontrol calls
        const onMapActionDelete = async () => {
            await sidebar.value.removeCurrentZone();
        }

        const onMapActionImport = async () => {

            // auto remove existing zone
            await sidebar.value.removeCurrentZone();

            sidebar.value.onZoneImport();
        }
        //
        ////////////////////////////////////////////////////////////////////////////////


        return {
            allowMultiPolygons,

            isLoading,
            kmhmEnabled,
            kmhmHasError,
            kmhmIsLoading,

            disableDeleteAction,
            showSearch,

            updateInputGeometries,

            toggleKmHmLayer,

            onStateChange,
            onZoomToFeature,
            onZoomToFeatures,
            onLoading,
            onInput,
            onZonePrice,
            onSearchOnExtent,
            onSearchOnCoordinate,

            onKlbToggleShowActive,

            onRemoveGeometry,

            getCurrentVisibleArea,
            //setCurrentVisibleArea,

            onMapActionDelete,
            onMapActionImport,
        }
    }
})
